/**
 * Mobile experience — single on/off switch.
 *
 * ROLLBACK CONTRACT
 * -----------------
 * The entire mobile UI is gated by `useMobileExperience()`. It returns true only
 * when BOTH of these hold:
 *   1. the viewport is phone-sized (<= 767px), and
 *   2. the kill-switch is not disabled.
 *
 * To roll back instantly with NO code change:
 *   - set localStorage `pa_mobile_ui = "off"`  (per-device), or
 *   - set `window.__PA_MOBILE_UI__ = false` before render, or
 *   - build with `VITE_MOBILE_UI=off`.
 *
 * To roll back in code: revert the single conditional in
 * `src/components/prioritization/Layout.tsx` and delete `src/mobile/`.
 *
 * Desktop (>= 768px) is NEVER affected by anything in this directory.
 */
import { useMediaQuery } from './useMediaQuery';

/** Phone breakpoint. Tablets (>=768px) keep the desktop shell. */
export const MOBILE_MAX_WIDTH = 767;
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_MAX_WIDTH}px)`;

const LS_KEY = 'pa_mobile_ui';

function killSwitchEnabled(): boolean {
  // Build-time override
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  if (env?.VITE_MOBILE_UI === 'off') return false;

  if (typeof window !== 'undefined') {
    // Runtime override (e.g. set in console for emergency rollback)
    const w = window as unknown as { __PA_MOBILE_UI__?: boolean };
    if (w.__PA_MOBILE_UI__ === false) return false;
    if (w.__PA_MOBILE_UI__ === true) return true;
    try {
      const ls = window.localStorage?.getItem(LS_KEY);
      if (ls === 'off') return false;
      if (ls === 'on') return true;
    } catch {
      /* localStorage may be unavailable — fall through to default */
    }
  }
  return true; // default ON for phone-sized viewports
}

/** True when the bespoke mobile shell should render instead of the desktop shell. */
export function useMobileExperience(): boolean {
  const isPhone = useMediaQuery(MOBILE_MEDIA_QUERY);
  return isPhone && killSwitchEnabled();
}

/** Imperative escape hatches for support/QA. */
export const mobileUi = {
  disableForThisDevice() {
    try {
      window.localStorage?.setItem(LS_KEY, 'off');
    } catch { /* no-op */ }
  },
  enableForThisDevice() {
    try {
      window.localStorage?.setItem(LS_KEY, 'on');
    } catch { /* no-op */ }
  },
  reset() {
    try {
      window.localStorage?.removeItem(LS_KEY);
    } catch { /* no-op */ }
  },
};
